import React, { useCallback, useEffect, useId, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Building2, Loader2, Plus } from 'lucide-react';
import { useAuth } from '../hooks/useAuth';
import { organizationAPI } from '../services/api';
import { confirmDiscard, hasUnsavedChanges } from '../utils/unsavedChanges';
import OrgCatalogManager from './OrgCatalogManager';
import OrgPromotionsManager from './OrgPromotionsManager';
import OrgUpsellManager from './OrgUpsellManager';
import OrgPlatformMappingsTab from './OrgPlatformMappingsTab';
import ExportReportModal from './analytics/ExportReportModal';
import { useToast } from './shared/Toast';
import { LoadFailed } from './settings/instantSettings';
import { SheetField } from './settings/sheet';
import { DetailPanel, EmptyState, GHOST_BTN, INPUT_CLS, Notice, PRIMARY_BTN } from './shared/listPage';
import OrgShell, { OrgTabId, visibleOrgTabs } from './org/OrgShell';
import OrgOverviewTab from './org/OrgOverviewTab';
import OrgStoresTab from './org/OrgStoresTab';
import OrgOrdersTab from './org/OrgOrdersTab';
import OrgMembersTab, { NewMemberDraft } from './org/OrgMembersTab';
import OrgSettingsTab from './org/OrgSettingsTab';
import { useOrgAnalytics } from './org/useOrgAnalytics';
import { useOrgOrders } from './org/useOrgOrders';
import { LoadError, OrgMember, OrgStore, Organization, PriceListRef, loadErrorOf, serverMessage } from './org/orgTypes';
import { TFn, roleLabel } from './org/orgFormat';

/**
 * Τα κεντρικά (χρήστης οργανισμού). Νέα εμφάνιση (14/10/2026): πλαίσιο με κεφαλίδα και πλαϊνό μενού σε
 * ομάδες (org/OrgShell, όπως η διαχείριση καταστήματος) και κάθε σελίδα στη γλώσσα της εφαρμογής — Αρχική
 * ως «Ήσυχο ταμπλό», Καταστήματα/Παραγγελίες/Μέλη ως «Πίνακας» με συρτάρι, Ρυθμίσεις ως «Φύλλο».
 *
 * ΜΗΔΕΝ αλλαγή συμπεριφοράς: εδώ μένουν τα δεδομένα και οι ενέργειες ΟΠΩΣ ήταν (ίδια endpoints, ίδια
 * σώματα, ίδιοι κανόνες ρόλων org_owner/org_admin/org_viewer). Οι καρτέλες Κατάλογος/Προσφορές/Προτάσεις
 * πώλησης/Πλατφόρμες/Κατάλογος πλατφορμών παίρνουν ΑΚΡΙΒΩΣ τα ίδια props με πριν.
 */

const OrgDashboard: React.FC = () => {
  const { t: tRaw } = useTranslation(['orgDashboard', 'admin', 'common', 'listPage']);
  const t = tRaw as unknown as TFn;
  const { employee, logout } = useAuth();
  const { showToast } = useToast();
  const uid = useId();

  const [orgs, setOrgs] = useState<Organization[]>([]);
  const [selectedOrg, setSelectedOrg] = useState<Organization | null>(null);
  const [stores, setStores] = useState<OrgStore[]>([]);
  const [members, setMembers] = useState<OrgMember[]>([]);
  const [myRole, setMyRole] = useState<string>('org_viewer');
  // Η πρώτη φόρτωση δείχνει τη σελίδα αναμονής· οι επόμενες (μετά από αλλαγή ρυθμίσεων κ.λπ.) κρατούν τη σελίδα.
  const [ready, setReady] = useState(false);
  const [reloading, setReloading] = useState(false);
  const [listError, setListError] = useState<LoadError | null>(null);
  const [orgError, setOrgError] = useState<LoadError | null>(null);
  const [activeTab, setActiveTab] = useState<OrgTabId>('overview');
  const [switching, setSwitching] = useState<number | null>(null);
  const [exporting, setExporting] = useState(false);
  // Τιμοκατάλογοι για την ανάθεση στα καταστήματα
  const [priceLists, setPriceLists] = useState<PriceListRef[]>([]);

  // Νέος οργανισμός (μόνο όταν δεν υπάρχει κανένας — όπως πριν)
  const [showCreateOrg, setShowCreateOrg] = useState(false);
  const [newOrg, setNewOrg] = useState({ name: '', slug: '', contact_email: '', contact_phone: '' });
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);

  // Παραμετροποιήσιμη αναφορά (PDF/Excel)
  const [showReportModal, setShowReportModal] = useState(false);
  const [reportFormat, setReportFormat] = useState<'pdf' | 'excel'>('pdf');

  const analytics = useOrgAnalytics(selectedOrg);
  const orders = useOrgOrders(selectedOrg, stores, activeTab === 'orders');
  const { setFilterStoreIds, setData: setAnalyticsData, resolve: resolveRange, filterStoreIds } = analytics;

  // Τι φαίνεται ήδη: μια αποτυχία φόρτωσης ΠΑΝΩ σε ό,τι φαίνεται γίνεται μήνυμα, όχι σελίδα σφάλματος.
  const selectedOrgRef = useRef<Organization | null>(null);
  selectedOrgRef.current = selectedOrg;
  const orgsCountRef = useRef(0);
  orgsCountRef.current = orgs.length;
  const toastRef = useRef(showToast);
  toastRef.current = showToast;
  const tRef = useRef(t);
  tRef.current = t;

  const selectOrg = useCallback(async (org: Organization) => {
    try {
      setFilterStoreIds([]); // νέος οργανισμός → χωρίς απομόνωση καταστημάτων
      const data = await organizationAPI.get(org.id);
      setSelectedOrg(data.organization);
      setStores(data.stores || []);
      setMembers(data.members || []);
      setMyRole(data.my_role || 'org_viewer');
      setOrgError(null);

      // Τα νούμερα ανά κατάστημα (orders_today/revenue_today) έρχονται από το dashboard
      const dashData = await organizationAPI.dashboard(org.id);
      if (dashData.stores) setStores(dashData.stores);
      // Τιμοκατάλογοι (για την ανάθεση στα καταστήματα)
      organizationAPI.getPriceLists(org.id).then(d => setPriceLists(d.price_lists || [])).catch(() => setPriceLists([]));
    } catch (err) {
      console.error('Failed to load org:', err);
      if (selectedOrgRef.current) toastRef.current('error', tRef.current('orgDashboard:shell.orgLoadFailed'));
      else setOrgError(loadErrorOf(err));
    }
  }, [setFilterStoreIds]);

  const loadOrgs = useCallback(async () => {
    try {
      setReloading(true);
      const data = await organizationAPI.list();
      setOrgs(data.organizations || []);
      setListError(null);
      // Αυτόματα ο πρώτος οργανισμός (όπως πριν)
      if (data.organizations?.length > 0) {
        await selectOrg(data.organizations[0]);
      }
    } catch (err) {
      console.error('Failed to load orgs:', err);
      if (orgsCountRef.current > 0) toastRef.current('error', tRef.current('orgDashboard:shell.loadFailed'));
      else setListError(loadErrorOf(err));
    } finally {
      setReloading(false);
      setReady(true);
    }
  }, [selectOrg]);

  useEffect(() => {
    loadOrgs();
  }, [loadOrgs]);

  // Ο παρατηρητής δεν έχει Μέλη/Κατάλογο/Προσφορές/Προτάσεις/Πλατφόρμες — αν η ενεργή καρτέλα δείχνει εκεί
  // (π.χ. μετά από αλλαγή σε οργανισμό όπου είναι μόνο παρατηρητής), πίσω στην Αρχική αντί για κενή σελίδα.
  // Η λίστα πρέπει να είναι ΙΔΙΑ με τις καρτέλες που κρύβονται από τον viewer.
  useEffect(() => {
    if (myRole === 'org_viewer' && ['members', 'catalog', 'promotions', 'upsell', 'platforms'].includes(activeTab)) {
      setActiveTab('overview');
    }
  }, [myRole, activeTab]);

  const canManage = myRole === 'org_owner' || myRole === 'org_admin';
  const tabs = useMemo(() => (selectedOrg ? visibleOrgTabs(myRole) : []), [selectedOrg, myRole]);

  // Η σελίδα που κλείνει χάνει ό,τι δεν αποθηκεύτηκε (utils/unsavedChanges): ρώτα πρώτα, όπως η διαχείριση καταστήματος.
  const handleTabClick = (tab: OrgTabId) => {
    if (tab === activeTab) return;
    if (hasUnsavedChanges() && !confirmDiscard()) return;
    setActiveTab(tab);
  };
  const handleSelectOrg = (orgId: number) => {
    const org = orgs.find(o => o.id === orgId);
    if (!org) return;
    if (hasUnsavedChanges() && !confirmDiscard()) return;
    selectOrg(org);
  };
  const handleLogout = () => {
    if (hasUnsavedChanges() && !confirmDiscard()) return;
    logout();
  };

  /* ───────── αναφορές / εξαγωγή (ίδιες κλήσεις) ───────── */

  // analytics → { orders: {...} } (το σχήμα που περιμένουν οι εξαγωγείς, όπως στο κατάστημα).
  const reportAnalytics = useMemo(() => {
    const a = analytics.data;
    if (!a) return { orders: {} };
    const k: any = a.kpis || {};
    const change = (cur: number, prev: number) => (prev > 0 ? ((cur - prev) / prev) * 100 : null);
    const peak = [...(a.by_hour || [])].sort((x: any, y: any) => (y.orders || 0) - (x.orders || 0))[0];
    return {
      orders: {
        total_orders: k.orders ?? 0,
        total_revenue: k.revenue ?? 0,
        average_order_value: k.aov ?? 0,
        comparison: { ordersChange: change(k.orders, k.orders_prev), revenueChange: change(k.revenue, k.revenue_prev) },
        peak_hours: peak ? [{ hour: `${peak.hour}:00`, orders: peak.orders }] : [],
        daily_trend: (a.timeseries || []).map((d: any) => ({ date: d.date, orders: d.orders, revenue: d.revenue })),
        payment_methods: (a.by_payment || []).map((p: any) => ({ method: p.method, count: p.count, amount: p.amount })),
      },
    };
  }, [analytics.data]);

  const fetchOrgReportData = useCallback(async (sections: string[], detail: 'summary' | 'full') => {
    if (!selectedOrg) return null;
    const { from, to } = resolveRange();
    return organizationAPI.reportData(selectedOrg.id, from, to, filterStoreIds, sections.join(','), detail);
  }, [selectedOrg, resolveRange, filterStoreIds]);

  const openReport = (format: 'pdf' | 'excel') => {
    if (!selectedOrg) return;
    setReportFormat(format);
    setShowReportModal(true);
  };

  // Το Excel συναλλαγή × προϊόν της περιόδου που φαίνεται.
  const handleExportExcel = async () => {
    if (!selectedOrg || exporting) return;
    const { from, to } = resolveRange();
    if (from > to) { showToast('error', t('orgDashboard:overview.export.badRange')); return; }
    setExporting(true);
    try {
      const resp = await organizationAPI.exportTransactionProducts(selectedOrg.id, from, to, filterStoreIds);
      const blob = new Blob([resp.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
      });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `transactionProductReport_${from}_${to}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err: any) {
      // Το σώμα του σφάλματος είναι Blob (responseType: 'blob') — διαβάζουμε το μήνυμα από το JSON.
      let msg = t('orgDashboard:overview.export.failed');
      try {
        const text = await err?.response?.data?.text?.();
        if (text) msg = JSON.parse(text).error || msg;
      } catch { /* μένει το εφεδρικό */ }
      showToast('error', msg);
    } finally {
      setExporting(false);
    }
  };

  /* ───────── καταστήματα (ίδιες κλήσεις) ───────── */

  const handleSetStoreLabel = async (storeId: number, value: string) => {
    if (!selectedOrg) return;
    const display_name = value.trim() || null;
    try {
      await organizationAPI.updateStoreLabel(selectedOrg.id, storeId, display_name);
      setStores(prev => prev.map(s => (s.id === storeId ? { ...s, display_name } : s)));
      setAnalyticsData(prev => (prev ? { ...prev, by_store: (prev.by_store || []).map(s => (s.id === storeId ? { ...s, display_name } : s)) } : prev));
    } catch (err) {
      console.error('Failed to set store label:', err);
      showToast('error', serverMessage(err, t('admin:failedUpdate2')));
    }
  };

  const handleAssignPriceList = async (storeId: number, priceListId: string) => {
    if (!selectedOrg) return;
    try {
      await organizationAPI.assignStorePriceList(selectedOrg.id, storeId, priceListId ? parseInt(priceListId, 10) : null);
      setStores(prev => prev.map(s => (s.id === storeId ? { ...s, price_list_id: priceListId ? parseInt(priceListId, 10) : null } : s)));
    } catch (err) {
      console.error('Failed to assign price list:', err);
      showToast('error', serverMessage(err, t('admin:failedUpdate2')));
    }
  };

  const handleSwitchStore = async (storeId: number) => {
    if (!selectedOrg) return;
    try {
      setSwitching(storeId);
      const data = await organizationAPI.switchStore(selectedOrg.id, storeId);
      // Νέο token και φόρτωση από την αρχή (όπως πριν)
      localStorage.setItem('xentrix_token', data.token);
      window.location.href = '/';
    } catch (err) {
      console.error('Failed to switch store:', err);
      setSwitching(null);
      showToast('error', serverMessage(err, t('orgDashboard:stores.switchFailed')));
    }
  };

  const handleAddStore = async (storeIdToAdd: string, priceListToAdd: string): Promise<string | null> => {
    if (!selectedOrg || !storeIdToAdd) return null;
    let data: any;
    try {
      data = await organizationAPI.addStore(selectedOrg.id, parseInt(storeIdToAdd, 10), priceListToAdd ? parseInt(priceListToAdd, 10) : null);
    } catch (err: any) {
      return err.response?.data?.error || t('admin:failedAdd2');
    }
    setStores(data.stores || []);
    // Το παράθυρο κλείνει αμέσως· ο οργανισμός ξαναφορτώνεται από πίσω (όπως πριν).
    selectOrg(selectedOrg);
    return null;
  };

  const handleRemoveStore = async (storeId: number) => {
    if (!selectedOrg) return;
    try {
      const data = await organizationAPI.removeStore(selectedOrg.id, storeId);
      setStores(data.stores || []);
      await selectOrg(selectedOrg);
    } catch (err: any) {
      showToast('error', err.response?.data?.error || t('admin:failedRemove2'));
    }
  };

  /* ───────── μέλη (ίδιες κλήσεις) ───────── */

  const handleAddMember = async (draft: { org_user_id: string; role: string; store_ids: number[] }): Promise<string | null> => {
    if (!selectedOrg || !draft.org_user_id) return null;
    try {
      const data = await organizationAPI.addMember(
        selectedOrg.id, parseInt(draft.org_user_id, 10), draft.role,
        draft.store_ids.length > 0 ? draft.store_ids : null,
      );
      setMembers(data.members || []);
      return null;
    } catch (err: any) {
      return err.response?.data?.error || t('admin:failedAdd2');
    }
  };

  // Νέο μέλος (15/10/2026): χρήστης οργανισμού + συμμετοχή μαζί· μήνυμα σφάλματος του server όπως έρχεται.
  const handleCreateMember = async (draft: NewMemberDraft): Promise<string | null> => {
    if (!selectedOrg) return null;
    try {
      const data = await organizationAPI.createMember(selectedOrg.id, {
        first_name: draft.first_name, last_name: draft.last_name, email: draft.email,
        phone: draft.phone || undefined, password: draft.password, role: draft.role,
        store_ids: draft.store_ids.length > 0 ? draft.store_ids : null,
      });
      setMembers(data.members || []);
      showToast('success', t('orgDashboard:members.created', { email: draft.email.trim().toLowerCase() }));
      return null;
    } catch (err: any) {
      return err.response?.data?.error || t('admin:failedAdd2');
    }
  };

  // Η πρόσβαση ενός μέλους ([] = όλα τα καταστήματα)· ο ρόλος μένει ο ίδιος.
  const handleSaveMemberScope = async (member: OrgMember, selected: number[]): Promise<boolean> => {
    if (!selectedOrg) return false;
    try {
      const data = await organizationAPI.updateMemberRole(selectedOrg.id, member.org_user_id, member.role, selected.length > 0 ? selected : null);
      setMembers(data.members || []);
      return true;
    } catch (err: any) {
      showToast('error', err.response?.data?.error || t('admin:failedUpdate2'));
      return false;
    }
  };

  const handleRemoveMember = async (orgUserId: number): Promise<boolean> => {
    if (!selectedOrg) return false;
    try {
      const data = await organizationAPI.removeMember(selectedOrg.id, orgUserId);
      setMembers(data.members || []);
      return true;
    } catch (err: any) {
      showToast('error', err.response?.data?.error || t('admin:failedRemove2'));
      return false;
    }
  };

  const handleUpdateMemberRole = async (orgUserId: number, role: string): Promise<boolean> => {
    if (!selectedOrg) return false;
    try {
      const data = await organizationAPI.updateMemberRole(selectedOrg.id, orgUserId, role);
      setMembers(data.members || []);
      return true;
    } catch (err: any) {
      showToast('error', err.response?.data?.error || t('admin:failedUpdate2'));
      return false;
    }
  };

  /* ───────── νέος οργανισμός ───────── */

  const closeCreate = () => {
    setShowCreateOrg(false);
    setCreateError(null);
  };

  const handleCreateOrg = async (e?: React.FormEvent) => {
    e?.preventDefault();
    if (!newOrg.name || !newOrg.slug || creating) return;
    try {
      setCreating(true);
      setCreateError(null);
      await organizationAPI.create(newOrg);
      setShowCreateOrg(false);
      setNewOrg({ name: '', slug: '', contact_email: '', contact_phone: '' });
      await loadOrgs();
    } catch (err: any) {
      setCreateError(err.response?.data?.error || t('admin:failedCreate2'));
    } finally {
      setCreating(false);
    }
  };

  /* ───────── σελίδα ───────── */

  if (!ready) {
    return (
      <div className="min-h-screen bg-surface-0 flex items-center justify-center" data-testid="org-loading">
        <div className="text-center">
          <Loader2 className="h-10 w-10 text-brand animate-spin mx-auto" aria-hidden="true" />
          <p className="mt-4 text-t3">{t('admin:loadingOrgs')}</p>
        </div>
      </div>
    );
  }

  const meta = selectedOrg ? t('admin:storesAndMembers', { stores: stores.length, members: members.length }) : null;

  let content: React.ReactNode;
  if (listError && orgs.length === 0) {
    content = (
      <div className="max-w-xl mx-auto mt-10" data-testid="org-list-error">
        <LoadFailed
          message={listError === 'forbidden' ? t('orgDashboard:shell.forbidden') : t('orgDashboard:shell.loadFailed')}
          onRetry={loadOrgs}
        />
      </div>
    );
  } else if (orgs.length === 0) {
    content = (
      <div className="lp-wrap max-w-xl mx-auto mt-10" data-testid="org-empty">
        <EmptyState
          icon={Building2}
          title={t('admin:noOrgTitle')}
          hint={t('admin:noOrgDescription')}
          action={(
            <button type="button" className={PRIMARY_BTN} onClick={() => setShowCreateOrg(true)} data-testid="org-create">
              <Plus className="w-4 h-4" aria-hidden="true" />
              {t('admin:createOrganization')}
            </button>
          )}
        />
      </div>
    );
  } else if (!selectedOrg) {
    // Χωρίς επιλεγμένο: είτε απέτυχε η φόρτωσή του, είτε ξαναφορτώνει (π.χ. μόλις διαγράφηκε ένας).
    content = orgError ? (
      <div className="max-w-xl mx-auto mt-10" data-testid="org-load-error">
        <LoadFailed
          message={orgError === 'forbidden' ? t('orgDashboard:shell.orgForbidden') : t('orgDashboard:shell.orgLoadFailed')}
          onRetry={loadOrgs}
        />
      </div>
    ) : (
      <div className="flex justify-center py-20" role="status" data-testid="org-reloading">
        <Loader2 className="w-8 h-8 text-t3 animate-spin" aria-hidden="true" />
        <span className="sr-only">{t('common:loading')}</span>
      </div>
    );
  } else {
    const org = selectedOrg;
    content = (
      <>
        {activeTab === 'overview' && (
          <OrgOverviewTab
            analytics={analytics}
            stores={stores}
            switching={switching}
            onSwitchStore={handleSwitchStore}
            onOpenReport={openReport}
            onExportExcel={handleExportExcel}
            exporting={exporting}
          />
        )}

        {activeTab === 'stores' && (
          <OrgStoresTab
            orgId={org.id}
            stores={stores}
            priceLists={priceLists}
            canManage={canManage}
            switching={switching}
            onSwitchStore={handleSwitchStore}
            onSetStoreLabel={handleSetStoreLabel}
            onAssignPriceList={handleAssignPriceList}
            onAddStore={handleAddStore}
            onRemoveStore={handleRemoveStore}
          />
        )}

        {/* Παραγγελίες & παραστατικά — κεντρική εικόνα + ακύρωση μόνο από τα κεντρικά */}
        {activeTab === 'orders' && (
          <OrgOrdersTab orgId={org.id} orgSettings={org.settings} stores={stores} canManage={canManage} orders={orders} />
        )}

        {activeTab === 'members' && myRole !== 'org_viewer' && (
          <OrgMembersTab
            members={members}
            stores={stores}
            canManage={canManage}
            onUpdateRole={handleUpdateMemberRole}
            onSaveScope={handleSaveMemberScope}
            onRemove={handleRemoveMember}
            onAdd={handleAddMember}
            onCreate={handleCreateMember}
          />
        )}

        {/* Κατάλογος */}
        {activeTab === 'catalog' && (
          <OrgCatalogManager orgId={org.id} orgName={org.name} canManage={canManage} />
        )}

        {/* Προσφορές */}
        {activeTab === 'promotions' && (
          <OrgPromotionsManager orgId={org.id} canManage={canManage} />
        )}

        {/* Προτάσεις πώλησης — διακόπτης/διαγραφή μόνο owner/admin (org_admin στο API), προτάσεις και σειρά από
            org_editor και πάνω. */}
        {activeTab === 'upsell' && myRole !== 'org_viewer' && (
          <OrgUpsellManager
            orgId={org.id}
            canEdit={canManage || myRole === 'org_editor'}
            canAdmin={canManage}
          />
        )}

        {/* Αντιστοιχίσεις ονομάτων πλατφορμών — οι αποφάσεις από org_editor και πάνω, η ΛΕΙΤΟΥΡΓΙΑ μόνο
            από owner/admin (docs/platform_name_mapping.md §8). */}
        {activeTab === 'platforms' && myRole !== 'org_viewer' && (
          <OrgPlatformMappingsTab
            orgId={org.id}
            canManage={canManage}
            canEdit={canManage || myRole === 'org_editor'}
          />
        )}

        {/* «Κατάλογος πλατφορμών» και «Εκδόσεις agent»: μόνο στον super admin από 15/10/2026 (SuperAdminOrgTools). */}

        {activeTab === 'settings' && canManage && (
          <OrgSettingsTab
            key={org.id}
            org={org}
            isOwner={myRole === 'org_owner'}
            onReload={loadOrgs}
            onThemeSaved={theme => {
              setSelectedOrg(prev => (prev && prev.id === org.id ? { ...prev, theme } : prev));
              setOrgs(prev => prev.map(o => (o.id === org.id ? { ...o, theme } : o)));
            }}
            onDeleted={async () => {
              setSelectedOrg(null);
              await loadOrgs();
            }}
          />
        )}

        {/* Παραμετροποιήσιμη αναφορά (PDF/Excel) — επίπεδο οργανισμού, χωρίς πελάτες */}
        <ExportReportModal
          isOpen={showReportModal}
          onClose={() => setShowReportModal(false)}
          storeId={0}
          storeName={org.name}
          dateFrom={resolveRange().from}
          dateTo={resolveRange().to}
          analytics={reportAnalytics}
          initialFormat={reportFormat}
          fetchReportData={fetchOrgReportData}
          hideCustomers
        />
      </>
    );
  }

  return (
    <OrgShell
      title={selectedOrg ? selectedOrg.name : t('admin:organizations')}
      meta={meta}
      orgs={orgs}
      selectedOrgId={selectedOrg?.id ?? null}
      onSelectOrg={handleSelectOrg}
      userName={employee?.name}
      userRole={selectedOrg ? roleLabel(t, myRole) : null}
      onLogout={handleLogout}
      tabs={tabs}
      activeTab={activeTab}
      onTabClick={handleTabClick}
      busy={reloading}
    >
      {content}

      {/* Νέος οργανισμός */}
      <DetailPanel
        open={showCreateOrg}
        onClose={closeCreate}
        title={t('admin:newOrgTitle')}
        subtitle={t('orgDashboard:shell.createSubtitle')}
        testId="org-create-panel"
        footer={(
          <>
            <button type="button" className={GHOST_BTN} onClick={closeCreate}>{t('orgDashboard:actions.cancel')}</button>
            <button type="submit" form={`${uid}-create`} className={PRIMARY_BTN} disabled={creating || !newOrg.name || !newOrg.slug} data-testid="org-create-submit">
              {creating && <Loader2 className="w-4 h-4 animate-spin" aria-hidden="true" />}
              {creating ? t('admin:creating2') : t('common:create')}
            </button>
          </>
        )}
      >
        <form id={`${uid}-create`} onSubmit={handleCreateOrg} noValidate className="space-y-5">
          <SheetField label={`${t('admin:orgNameLabel')} *`} htmlFor={`${uid}-name`}>
            <input
              id={`${uid}-name`}
              value={newOrg.name}
              onChange={e => setNewOrg({ ...newOrg, name: e.target.value, slug: e.target.value.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/(^-|-$)/g, '') })}
              className={INPUT_CLS}
              placeholder="Coffee Island"
              autoFocus
              data-testid="org-create-name"
            />
          </SheetField>
          <SheetField label={`${t('admin:orgSlugLabel')} *`} htmlFor={`${uid}-slug`}>
            <input id={`${uid}-slug`} value={newOrg.slug} onChange={e => setNewOrg({ ...newOrg, slug: e.target.value })} className={INPUT_CLS} placeholder="coffee-island" data-testid="org-create-slug" />
          </SheetField>
          <SheetField label={t('common:email')} htmlFor={`${uid}-email`}>
            <input id={`${uid}-email`} value={newOrg.contact_email} onChange={e => setNewOrg({ ...newOrg, contact_email: e.target.value })} className={INPUT_CLS} />
          </SheetField>
          <SheetField label={t('admin:contactPhoneLabel')} htmlFor={`${uid}-phone`}>
            <input id={`${uid}-phone`} value={newOrg.contact_phone} onChange={e => setNewOrg({ ...newOrg, contact_phone: e.target.value })} className={INPUT_CLS} />
          </SheetField>
          {createError && <Notice tone="danger" testId="org-create-error">{createError}</Notice>}
        </form>
      </DetailPanel>
    </OrgShell>
  );
};

export default OrgDashboard;
